import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional per-route label used in telemetry and the fallback message. */
  scope?: string;
  /** If true, exposes a 'Back to desks' link in the fallback (desk routes). */
  showHomeLink?: boolean;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Catches render-phase errors in its subtree and shows a friendly fallback
 * instead of a blank white page. Errors are logged to the console (so Vite
 * HMR / devtools can pick them up) and we record the scope so the fallback
 * can nudge the user back to a known-good view.
 *
 * Reset on route change: the top-level router key changes per navigation
 * in the parent Outlet, which unmounts + remounts this boundary. That
 * means a user can escape a crashed screen just by navigating away.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ info });
    console.error(
      `[ErrorBoundary${this.props.scope ? ` · ${this.props.scope}` : ""}]`,
      error,
      info.componentStack,
    );
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="h-full flex flex-col items-center justify-center px-6 text-center"
        style={{ background: "var(--bg)" }}
      >
        <div className="max-w-md space-y-3">
          <AlertTriangle
            size={28}
            className="mx-auto"
            style={{ color: "var(--err)" }}
          />
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-xl)",
              fontWeight: 600,
              color: "var(--t1)",
              letterSpacing: "-0.3px",
            }}
          >
            Something went wrong
          </h1>
          <p className="text-[13px]" style={{ color: "var(--t3)" }}>
            The view crashed while rendering{" "}
            {this.props.scope ? (
              <code
                className="px-1.5 py-0.5 rounded"
                style={{
                  background: "var(--hover)",
                  fontFamily: "var(--font-mono)",
                  fontSize: "11px",
                }}
              >
                {this.props.scope}
              </code>
            ) : (
              "this screen"
            )}
            . Your data is safe — just the UI hiccupped. Try reloading, or go
            back to the dashboard.
          </p>

          <details
            className="text-left mt-4"
            style={{
              background: "var(--white)",
              border: "1px solid var(--border)",
              borderRadius: "6px",
              padding: "8px 12px",
            }}
          >
            <summary
              className="cursor-pointer text-[11px]"
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--t3)",
                letterSpacing: "0.04em",
              }}
            >
              Error details
            </summary>
            <pre
              className="text-[11px] mt-2 whitespace-pre-wrap break-words"
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--err)",
                maxHeight: 240,
                overflow: "auto",
              }}
            >
              {error.message}
              {error.stack ? `\n\n${error.stack.slice(0, 2000)}` : ""}
            </pre>
          </details>

          <div className="flex items-center justify-center gap-2 pt-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="text-[12px] px-3 py-1.5 rounded"
              style={{
                background: "var(--accent)",
                color: "var(--white)",
              }}
            >
              Try again
            </button>
            {this.props.showHomeLink && (
              <Link
                to="/"
                className="text-[12px] px-3 py-1.5 rounded border"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--t2)",
                }}
              >
                Back to desks
              </Link>
            )}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-[12px] px-3 py-1.5 rounded border"
              style={{
                borderColor: "var(--border)",
                color: "var(--t2)",
              }}
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
